import {
  JsonApiCollection,
  JsonApiModel,
  ModelAttributes,
  ModelQuery,
  ModelRelationships,
  NewableCollection,
  NewableModel,
} from './types';
import { snakeCase } from 'lodash';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import Collection from './Collection';
import QueryBuilder from './QueryBuilder';
import deepFreeze from 'deep-freeze';
import { AxiosRequestConfig} from 'axios';
import { NuxtRuntimeConfig } from '@nuxt/types/config/runtime'

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(localizedFormat);

export default abstract class Model {
  static rootUri?: string;
  static $config: NuxtRuntimeConfig = {};

  public id?: string | number;

  protected _data?: JsonApiModel;

  constructor(_data?: JsonApiModel) {
    this._data = _data ? deepFreeze(JSON.parse(JSON.stringify(_data))) : _data;

    this.id = _data && _data.id;
    this.bind();

    if (['development', 'test'].includes(process.env.NODE_ENV || '')) {
      return new Proxy(this, {
        get(target, prop) {
          const allowedProps = [
            '_isVue',
            'children',
            'hasChildren',
            'render',
            'state',
            'then',
            '__ob__'
          ];
          if (typeof prop === 'string' && !allowedProps.includes(prop) && !(prop in target)) {
            console.error(
              `Cannot read undefined property '${String(prop)}' of ${target.constructor.name}`,
            );
          }

          // @ts-ignore
          return target[prop];
        },
      });
    }
  }

  static getConfig(): NuxtRuntimeConfig {
    return this.$config;
  }

  getConfig(): NuxtRuntimeConfig {
    return (this as any).constructor.getConfig();
  }

  static setConfig(config: NuxtRuntimeConfig) {
    this.$config = config;
  }

  get $config(){
    return this.getConfig();
  }

  abstract bind(): void;

  getClass(): NewableModel<Model> {
    return (this as any).constructor;
  }

  static getClass(): NewableModel<Model> {
    return this as any;
  }

  getCollection(): NewableCollection<Model> {
    return this.getClass().getCollection();
  }

  static getCollection(): NewableCollection<Model> {
    throw new Error('You have to implement the static method getModel');
  }

  getType(): string {
    if (this.getClass().type) return this.getClass().type as string;
    return snakeCase(this.getClass().name);
  }

  static getType(): string {
    if (this.getClass().type) return this.getClass().type as string;
    return snakeCase(this.getClass().name);
  }

  raw(): JsonApiModel | undefined {
    return JSON.parse(JSON.stringify(this._data));
  }

  clone(): Model {
    return new (this.getClass())(this.raw());
  }

  jsonSerialize(): JsonApiModel {
    const relationships = this.getJsonRelationships();
    return JSON.parse(
      JSON.stringify({
        id: this.getKey(),
        type: this.getType(),
        attributes: this.getJsonAttributes(),
        ...(relationships ? { relationships } : {}),
      }),
    );
  }

  toJSON() {
    const relationships = this.getJsonRelationships();
    return JSON.parse(
      JSON.stringify({
        id: this.getKey(),
        type: this.getType(),
        ...this.getJsonAttributes(),
        ...(relationships ? relationships : {}),
      }),
    );
  }

  getJsonRelationships(): ModelRelationships | undefined {
    if (!this._data) return;
    return this._data.relationships;
  }

  getKey(): string | number | undefined {
    if (!this._data) return;
    return this._data.id;
  }

  private getJsonAttributes(): ModelAttributes {
    return this.getOriginalAttributes();
  }

  isNew(): boolean {
    return !this.getKey();
  }

  getOriginalAttributes(): ModelAttributes {
    if (!this._data) return {};
    return this._data.attributes || {};
  }

  getOriginalAttribute(attribute: string, defaultValue?: any): any {
    const originalAttributes = this.getOriginalAttributes();
    if (originalAttributes[attribute] === undefined) return defaultValue;
    return originalAttributes[attribute];
  }

  hasOriginalAttribute(attribute: string): boolean {
    return ![null, undefined].includes(this.getOriginalAttribute(attribute));
  }

  getIntAttribute(attribute: string, defaultValue?: number): number | undefined {
    if (!this.hasOriginalAttribute(attribute)) return defaultValue;
    return Number.parseInt(this.getOriginalAttribute(attribute), 10);
  }

  getFloatAttribute(attribute: string, defaultValue?: number): number | undefined {
    if (!this.hasOriginalAttribute(attribute)) return defaultValue;
    return Number.parseFloat(this.getOriginalAttribute(attribute));
  }

  getStringAttribute(attribute: string, defaultValue?: string): string | undefined {
    if (!this.hasOriginalAttribute(attribute)) return defaultValue;
    return this.getOriginalAttribute(attribute).toString();
  }

  getDateAttribute(
    attribute: string,
    defaultValue?: string | dayjs.Dayjs,
  ): dayjs.Dayjs | undefined {
    if (!this.hasOriginalAttribute(attribute)) {
      return typeof defaultValue === 'string' ? dayjs(defaultValue) : defaultValue;
    }
    return dayjs(this.getOriginalAttribute(attribute));
  }

  getBooleanAttribute(attribute: string, defaultValue?: any): number | any {
    if (!this.hasOriginalAttribute(attribute)) return defaultValue;
    return ![false, 'false', 0, '0', ''].includes(this.getOriginalAttribute(attribute));
  }

  getRelation<T extends Model>(relation: string, model: NewableModel<T>): T | undefined {
    if (!this._data) return;
    const relationships = this._data.relationships;
    if (!relationships || [undefined, null].includes(relationships[relation] as any)) return;
    return new model(relationships[relation] as JsonApiModel) as T;
  }

  getRelationCollection<T extends Model>(
    relation: string,
    collection: NewableCollection<T>,
  ): Collection<T> {
    if (!this._data) return new collection();
    const relationships = this._data.relationships;
    if (!relationships || [undefined, null].includes(relationships[relation] as any)) {
      return new collection();
    }
    return new collection(relationships[relation] as JsonApiCollection) as Collection<T>;
  }

  getRelationAttribute<T extends Model>(relation: string, model: NewableModel<T>): T | undefined {
    const data = this.getOriginalAttribute(relation);
    if (!data) return;
    return new model(data) as T;
  }

  getRelationAttributeCollection<T extends Model>(
    relation: string,
    collection: NewableCollection<T>,
  ): Collection<T> {
    const data = this.getOriginalAttribute(relation);
    if (!data) return new collection();
    return new collection(data) as Collection<T>;
  }

  hasRelationship(): boolean {
    return !!(this._data && this._data.relationships);
  }

  static async find(
    id?: number | string,
    params?: ModelQuery,
    config?: AxiosRequestConfig,
  ): Promise<Model> {
    return this.QueryBuilder().find(id, params, config);
  }

  static async fetch(params?: ModelQuery, config?: AxiosRequestConfig): Promise<Collection<Model>> {
    return this.QueryBuilder().fetch(params, config);
  }

  static async search(
    search: string,
    params?: ModelQuery,
    config?: AxiosRequestConfig,
  ): Promise<Collection<Model>> {
    return this.QueryBuilder().search(search, params, config);
  }

  static async get(id?: string, config?: AxiosRequestConfig): Promise<Collection<Model>> {
    return this.QueryBuilder().get(id, config);
  }

  static async delete(id?: string, config?: AxiosRequestConfig): Promise<Collection<Model>> {
    return this.QueryBuilder().delete(id, config);
  }

  static async head(id?: string, config?: AxiosRequestConfig): Promise<Collection<Model>> {
    return this.QueryBuilder().head(id, config);
  }

  static async options(id?: string, config?: AxiosRequestConfig): Promise<Collection<Model>> {
    return this.QueryBuilder().options(id, config);
  }

  static async post(id?: string, config?: AxiosRequestConfig): Promise<Collection<Model>> {
    return this.QueryBuilder().post(id, config);
  }

  static async put(id?: string, config?: AxiosRequestConfig): Promise<Collection<Model>> {
    return this.QueryBuilder().put(id, config);
  }

  static async patch(id?: string, config?: AxiosRequestConfig): Promise<Collection<Model>> {
    return this.QueryBuilder().patch(id, config);
  }

  static with(relation: string) {
    return this.QueryBuilder().with(relation);
  }

  static where(column: string, value: any) {
    return this.QueryBuilder().where(column, value);
  }

  static orderBy(column?: string, direction: 'asc' | 'desc' = 'asc') {
    return this.QueryBuilder().orderBy(column, direction);
  }

  static paginate(perPage: number) {
    return this.QueryBuilder().paginate(perPage);
  }

  static page(page: number) {
    return this.QueryBuilder().page(page);
  }

  static QueryBuilder(path?: string) {
    return new QueryBuilder(this.getClass(), path);
  }

  QueryBuilder(path?: string) {
    return this.getClass().QueryBuilder(path);
  }

  static from(
    model: Model | [NewableModel<Model>, string | number | undefined],
    relation?: string,
  ) {
    return this.QueryBuilder().from(model, relation);
  }

  static withQuery(query: ModelQuery) {
    return this.QueryBuilder().withQuery(query);
  }
}
